/**
 * Every route of the CRM. Foundation-owned: areas never edit this file.
 *
 * Each area exports its routes from `routes/<area>/routes.tsx` (light: route objects
 * only); its pages load lazily from there. A new screen inside an area is added in that
 * area's routes.tsx and appears here automatically. Only a brand-new top-level area
 * needs a line here (and an item in navigation.ts).
 */
import { createBrowserRouter, type RouteObject } from 'react-router-dom';

import { RequireAuth } from '@/auth/RequireAuth';
import { SignInPage } from '@/auth/SignInPage';
import { AppShell } from '@/shell/AppShell';

import { accountRoutes } from './account/routes';
import { announcementsRoutes } from './announcements/routes';
import { clientsRoutes } from './clients/routes';
import { dashboardRoutes } from './dashboard/routes';
import { dietRoutes } from './diet/routes';
import { getAppRoutes } from './get-app/routes';
import { importRoutes } from './import/routes';
import { landingRoutes } from './landing/routes';
import { legalRoutes } from './legal/routes';
import { messagesRoutes } from './messages/routes';
import { NotFoundPage } from './NotFoundPage';
import { paymentsRoutes } from './payments/routes';
import { plansRoutes } from './plans/routes';
import { RouteErrorPage } from './RouteErrorPage';
import { setPasswordRoutes } from './set-password/routes';
import { settingsRoutes } from './settings/routes';
import { trialRoutes } from './trial/routes';
import { trainersRoutes } from './trainers/routes';
import { videosRoutes } from './videos/routes';

/** Signed-in areas, inside the shell. */
export const areaRoutes: RouteObject[] = [
  ...dashboardRoutes,
  ...clientsRoutes,
  ...messagesRoutes,
  ...plansRoutes,
  ...dietRoutes,
  ...videosRoutes,
  ...paymentsRoutes,
  ...announcementsRoutes,
  ...trainersRoutes,
  ...importRoutes,
  ...settingsRoutes,
  ...accountRoutes,
];

export const routes: RouteObject[] = [
  { path: '/login', element: <SignInPage />, errorElement: <RouteErrorPage /> },
  // Public pages, outside the shell.
  { path: '/', errorElement: <RouteErrorPage />, children: [...setPasswordRoutes, ...landingRoutes, ...trialRoutes, ...getAppRoutes, ...legalRoutes] },
  {
    path: '/',
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [{ errorElement: <RouteErrorPage />, children: [...areaRoutes, { path: '*', element: <NotFoundPage /> }] }],
      },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
